import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
// import { BASE_URL } from '../../constants';
import { BASE_URL } from '../constants';

function TaskList() {
  const { houseId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [house, setHouse] = useState(null);
  const [houses, setHouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formData, setFormData] = useState({
    category_id: '',
    sub_category_id: '',
    status_id: '',
  });
  const [editTask, setEditTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  // Fetch house details and all houses for the project
  useEffect(() => {
    setTasks([]);
    setHouse(null);
    setShowForm(false);
    setEditTask(null);
    resetForm();

    axios
      .get(`${BASE_URL}houses/${houseId}/`)
      .then((response) => {
        setHouse(response.data);
        axios
          .get(`${BASE_URL}houses/?project=${response.data.project.id}`)
          .then((res) => {
            const sortedHouses = res.data.sort((a, b) => {
              if (a.floor !== b.floor) {
                return a.floor - b.floor;
              }
              return a.house_id.localeCompare(b.house_id);
            });
            setHouses(sortedHouses);
          })
          .catch((error) => console.error('Error fetching houses:', error));
      })
      .catch((error) => console.error('Error fetching house:', error));
  }, [houseId]);

  // Fetch tasks for the house
  useEffect(() => {
    if (houseId) {
      axios
        .get(`${BASE_URL}tasks/?house_id=${houseId}`)
        .then((response) => setTasks(response.data))
        .catch((error) => console.error('Error fetching tasks:', error));
    }
  }, [houseId]);

  // Fetch categories
  useEffect(() => {
    axios
      .get(`${BASE_URL}categories/`)
      .then((response) => setCategories(response.data))
      .catch((error) => console.error('Error fetching categories:', error));
  }, []);

  // Fetch subcategories
  useEffect(() => {
    if (formData.category_id) {
      axios
        .get(`${BASE_URL}subcategories/?category=${formData.category_id}`)
        .then((response) => setSubCategories(response.data))
        .catch((error) => console.error('Error fetching subcategories:', error));
    } else {
      setSubCategories([]);
      setFormData((prev) => ({ ...prev, sub_category_id: '' }));
    }
  }, [formData.category_id]);

  // Fetch statuses
  useEffect(() => {
    axios
      .get(`${BASE_URL}statuses/`)
      .then((response) => setStatuses(response.data))
      .catch((error) => console.error('Error fetching statuses:', error));
  }, []);

  // Group tasks by category
  const groupTasksByCategory = (tasks) => {
    const grouped = tasks.reduce((acc, task) => {
      const category = task.category.name;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(task);
      return acc;
    }, {});
    // Sort categories alphabetically
    return Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});
  };

  // Calculate metrics for the executive summary
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((task) => task.status.name.toLowerCase() === 'completed').length;
  const completionPercentage = totalTasks > 0 ? ((completedTasks / totalTasks) * 100).toFixed(1) : 0;
  const overdueTasks = tasks.filter((task) => {
    const dueDate = task.due_date ? new Date(task.due_date) : null;
    return dueDate && dueDate < new Date() && task.status.name.toLowerCase() !== 'completed';
  }).length;

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const taskData = {
      house_id: houseId,
      category_id: formData.category_id,
      sub_category_id: formData.sub_category_id,
      status_id: formData.status_id,
    };

    if (editTask) {
      axios
        .put(`${BASE_URL}tasks/${editTask.id}/`, taskData)
        .then((response) => {
          setTasks(tasks.map((task) => (task.id === editTask.id ? response.data : task)));
          setEditTask(null);
          setShowForm(false);
          resetForm();
        })
        .catch((error) => console.error('Error updating task:', error));
    } else {
      axios
        .post(`${BASE_URL}tasks/`, taskData)
        .then((response) => {
          setTasks((tasks) => [...tasks, response.data]);
          setShowForm(false);
          resetForm();
        })
        .catch((error) => console.error('Error adding task:', error));
    }
  };

  // Handle task deletion
  const handleDelete = (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      axios
        .delete(`${BASE_URL}tasks/${taskId}/`)
        .then(() => {
          setTasks(tasks.filter((task) => task.id !== taskId));
        })
        .catch((error) => console.error('Error deleting task:', error));
    }
  };

  // Handle task editing
  const handleEdit = (task) => {
    setEditTask(task);
    setFormData({
      category_id: task.category.id,
      sub_category_id: task.sub_category.id,
      status_id: task.status.id,
    });
    setShowForm(true);
  };

  // Reset form fields
  const resetForm = () => {
    setFormData({
      category_id: '',
      sub_category_id: '',
      status_id: '',
    });
  };

  // Handle house selection
  const handleHouseSelect = (selectedHouseId) => {
    navigate(`/houses/${selectedHouseId}/tasks`);
  };

  const groupedTasks = groupTasksByCategory(tasks);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Side Menu */}
      <Drawer
        variant="permanent"
        sx={{
          width: 260,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: 260,
            boxSizing: 'border-box',
            marginTop: '64px',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #e0e0e0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
          },
        }}
      >
        <List>
          <ListSubheader
            sx={{
              backgroundColor: '#ffffff',
              fontWeight: 'bold',
              color: '#333',
              fontSize: '1.1rem',
              padding: '16px',
            }}
          >
            Houses in {house ? house.project.name : 'Project'}
          </ListSubheader>
          {houses.map((h) => (
            <ListItem
              key={h.id}
              button
              onClick={() => handleHouseSelect(h.id)}
              sx={{
                backgroundColor: h.id === parseInt(houseId) ? '#e3f2fd' : 'transparent',
                '&:hover': {
                  backgroundColor: h.id === parseInt(houseId) ? '#e3f2fd' : '#f5f5f5',
                },
                padding: '12px 24px',
                borderLeft:
                  h.id === parseInt(houseId) ? `4px solid ${theme => theme.palette.primary.main}` : 'none',
              }}
            >
              <ListItemText
                primary={`Floor ${h.floor === 0 ? 'Ground' : h.floor} - House ${h.house_id}`}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: h.id === parseInt(houseId) ? '600' : '400',
                  color: h.id === parseInt(houseId) ? theme => theme.palette.primary.main : '#333',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          padding: { xs: '16px', md: '32px' },
          marginLeft: { xs: 0, md: '8px' },
          backgroundColor: '#ffffff',
          maxWidth: '100%',
          overflowX: 'auto',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: '700', color: '#1a237e', mb: '24px' }}
        >
          Tasks for {house ? `${house.project.name} - Floor ${house.floor} - House ${house.house_id}` : 'House'}
        </Typography>

        {/* Executive Summary */}
        <Card sx={{ mb: '32px', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: '600', color: '#333', mb: '16px' }}
            >
              Executive Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body1" sx={{ color: '#555' }}>
                  <strong>Total Tasks:</strong> {totalTasks}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body1" sx={{ color: '#555' }}>
                  <strong>Completed:</strong> {completedTasks} ({completionPercentage}%)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body1" sx={{ color: '#555' }}>
                  <strong>Overdue:</strong> {overdueTasks}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body1" sx={{ color: '#555' }}>
                  <strong>Status:</strong>{' '}
                  {overdueTasks > 0 ? `${overdueTasks} overdue tasks need attention.` : 'All tasks on track.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ color: '#555', mt: 1 }}>
                  <strong>Recommendation:</strong>{' '}
                  {overdueTasks > 0
                    ? 'Prioritize overdue tasks and consider reallocating resources.'
                    : 'Continue monitoring task progress.'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Add Task Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setShowForm(true);
            setEditTask(null);
            resetForm();
          }}
          sx={{
            mb: '24px',
            textTransform: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          Add Task
        </Button>

        {/* Task Form */}
        {showForm && (
          <Paper
            sx={{
              padding: '24px',
              mb: '32px',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              backgroundColor: '#fafafa',
            }}
          >
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      required
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="">Select Category</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Sub-Category</InputLabel>
                    <Select
                      name="sub_category_id"
                      value={formData.sub_category_id}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.category_id}
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="">Select Sub-Category</MenuItem>
                      {subCategories.map((subCategory) => (
                        <MenuItem key={subCategory.id} value={subCategory.id}>
                          {subCategory.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status_id"
                      value={formData.status_id}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.category_id}
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: '12px' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: 'none', borderRadius: '8px', padding: '10px 20px' }}
                >
                  {editTask ? 'Update Task' : 'Add Task'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditTask(null);
                    resetForm();
                  }}
                  sx={{ textTransform: 'none', borderRadius: '8px', padding: '10px 20px' }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Task List by Category */}
        {tasks.length === 0 ? (
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', p: '24px', textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No tasks found. Click "Add Task" to create a new task.
            </Typography>
          </Card>
        ) : (
          Object.keys(groupedTasks).map((category) => (
            <Accordion
              key={category}
              sx={{
                mb: '16px',
                borderRadius: '12px !important',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                  padding: '0 16px',
                  '& .MuiAccordionSummary-content': { alignItems: 'center' },
                }}
              >
                <CategoryIcon sx={{ mr: 1, color: theme => theme.palette.primary.main }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: '600', color: theme => theme.palette.primary.main }}
                >
                  {category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5' }}>
                          Sub-Category
                        </TableCell>
                        <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5' }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5' }}>
                          Created At
                        </TableCell>
                        <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedTasks[category].map((task) => (
                        <TableRow
                          key={task.id}
                          sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                        >
                          <TableCell>{task.sub_category.name}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '12px',
                                backgroundColor: theme => theme.palette.primary.main + '1A',
                                color: theme => theme.palette.primary.main,
                                fontSize: '0.85rem',
                                fontWeight: '500',
                              }}
                            >
                              {task.status.name}
                            </Box>
                          </TableCell>
                          <TableCell>{new Date(task.created_at).toLocaleString()}</TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => handleEdit(task)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(task.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </Box>
  );
}

export default TaskList;